import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { PaginationBtnIcon } from '@/assets/icons/paginationBtnIcon';
import { useResponsive } from '../../hooks/use-responsive';
import Select from '../basicComponents/select';
import { createSimpleSelectOption } from '../basicComponents/select/utils';
import { initialPagination, initialValuesFromTable } from './constants';
import { IPagination } from './types';

export interface TablePaginationProps {
  savePagination?: (limit: number, offset: number) => void;
  amountRowsPerPage?: number[];
  paginationValues: IPagination | undefined;
  refetch: any;
  usePage?: boolean;
}

const TablePagination = ({
  savePagination,
  paginationValues,
  refetch,
  amountRowsPerPage = initialValuesFromTable.initialAmountRowsPerPage,
  usePage = false
}: TablePaginationProps) => {
  const { isMobile, isTablet, isPortrait } = useResponsive();
  const [{ limit, total, offset }, setPagination] = useState<IPagination>(initialPagination);

  useEffect(() => {
    if (paginationValues === undefined) return;
    const { limit, offset, total } = paginationValues;
    setPagination((pagination) => ({ ...pagination, limit, offset, total }));
  }, [paginationValues]);

  const updatePagination = <K extends keyof IPagination>(field: K, value: IPagination[K]) => {
    setPagination((prevPagination) => ({ ...prevPagination, [field]: value }));
    if (usePage) {
      const updatedOffset = field === 'offset' ? value : offset;
      const updatedLimit = field === 'limit' ? value : limit;
      refetch({
        page: updatedOffset / updatedLimit + 1,
        rowsPerPage: updatedLimit
      });
    } else {
      refetch({
        offset: field === 'offset' ? value : offset,
        limit: field === 'limit' ? value : limit
      });
    }
  };

  const nextPage = () => {
    if (offset + limit <= total) {
      updatePagination<'offset'>('offset', offset + limit);
      savePagination?.(limit, offset + limit);
    }
  };

  const prevPage = () => {
    if (offset - limit >= 0) {
      updatePagination<'offset'>('offset', offset - limit);
      savePagination?.(limit, offset - limit);
    }
  };

  const changeRowsPerPage = (value: string) => {
    updatePagination<'limit'>('limit', +value);
    savePagination?.(+value, offset);
  };

  return (
    <TablePaginationWrap isMobile={isMobile || isTablet || isPortrait}>
      <PaginationTitle>Rows per page:</PaginationTitle>
      <Select
        value={`${limit}`}
        setValue={changeRowsPerPage}
        options={amountRowsPerPage.map((amount) => createSimpleSelectOption(amount.toString()))}
        width="55px"
        size="sm"
      />
      {isMobile && <PaginationBtnIcon cursor="pointer" onClick={prevPage} />}
      <PaginationInfoWrap isMobile={isMobile}>
        {limit && (
          <PaginationInfo>
            {offset + 1}-{limit + offset < total ? limit + offset : total} of {total}
          </PaginationInfo>
        )}
      </PaginationInfoWrap>
      <ButtonsWrap>
        {!isMobile && <PaginationBtnIcon cursor="pointer" onClick={prevPage} />}
        <PaginationBtnIcon cursor="pointer" onClick={nextPage} rotate="rotate(180deg)" />
      </ButtonsWrap>
    </TablePaginationWrap>
  );
};

export default TablePagination;

const TablePaginationWrap = styled.div<{ isMobile: boolean }>`
  margin: 34px 0;
  display: flex;
  justify-content: ${({ isMobile }) => (isMobile ? 'center' : 'flex-end')} flex-end;
  align-items: center;
  gap: 14px;
`;

const PaginationTitle = styled.div`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  color: ${themeVar('font.base')};
`;

const ButtonsWrap = styled.div`
  display: flex;
  gap: 38px;
`;

const PaginationInfoWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  margin-right: ${({ isMobile }) => (isMobile ? 0 : '14px')};
`;

const PaginationInfo = styled.div`
  color: ${themeVar('font.base')};
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
`;
